import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Link,
  List,
  ListItem,
  Show,
  Text,
  VStack,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import { PiFilesThin, PiXThin } from 'react-icons/pi';
import SimpleCard from '../../presentational/Cards/SimpleCard';
import React, { ReactElement, useMemo } from 'react';
import { humanizedUserRole } from '../users/humanizedUserRole';
import { User } from '@/types/core/User';
import { useSelector } from 'react-redux';
import { ReduxRootState } from '@/types/store/ReduxRootState';
import userTranslations from '@/translations/user.fr.yml';
import useAccentColor from '@components/app/hooks/useAccentColor';
import SearchResultBadge from '../../presentational/Search/SearchResultBadge';
import ColleagueRelationship from '@components/app/users/ColleagueRelationship';
import RemoveColleagueRelationShip from '@components/app/users/RemoveColleagueRelationShip';
import ButtonWithConfirmTooltip from '@components/app/ButtonWithConfirmTooltip';
import { useRouter } from 'next/router';

const ROLE_TO_EXCLUDE = 'ROLE_USER';

type TMemberIDCardProps = {
  user: User;
  isCardFullWidth?: boolean;
  isMemberExpert?: boolean;
  variant?: string;
  showDetailLink?: boolean;
  showMemberRole?: boolean;
  badge?: ReactElement;
  canRemoveColleagueRelationship?: boolean;
  bg?: string;
};

export default function MemberIDCard({
  user,
  isCardFullWidth = false,
  isMemberExpert = false, // todo: remove when feature is no longer under development
  variant,
  showDetailLink = true,
  showMemberRole = true,
  badge = null,
  canRemoveColleagueRelationship = false,
  bg = null,
}: TMemberIDCardProps) {
  const Router = useRouter();

  const accentColor = useAccentColor(variant);
  const isVisitorTheMember = useSelector((state: ReduxRootState) => {
    return state.authentication.user?.id === user.id;
  });

  const [userRole, humanizedRole, isAttache]: [
    string,
    string,
    boolean
  ] = useMemo(() => {
    return [
      user.roles?.find(role => role !== ROLE_TO_EXCLUDE),
      humanizedUserRole(user),
      user.jobs?.includes('attache'),
    ];
  }, [user]);

  const badgeVariants = {
    ROLE_MEMBER: undefined,
    ROLE_PARTNER: undefined,
    ROLE_WORLD_ORGANISER: 'brandOutline',
    ROLE_COUNTRY_ORGANISER: 'brandOutline',
  };

  const jobsLabel = useMemo<string | null>(() => {
    if (user.jobs.length === 0) return null;
    return user.jobs
      .map(job => {
        return userTranslations['jobs'][`${job}`];
      })
      .join(', ');
  }, [user.jobs]);
  const disciplineLabels = useMemo<string[]>(() => {
    if (user.disciplines.length === 0) return [];
    return user.disciplines.map(discipline => {
      return userTranslations.discipline[`${discipline}`];
    });
  }, [user.disciplines]);

  return (
    <SimpleCard
      width="100%"
      height="100%"
      mt="0"
      accentColor={accentColor + '.100'}
      bg={bg}
      overflow="revert"
    >
      <Flex
        flexDirection={{ base: 'column', md: isCardFullWidth ? 'row' : null }}
        width="100%"
        justify={{ md: 'space-between' }}
        alignContent="space-between"
        height="100%"
        gap={{ base: '1rem', md: 0 }}
      >
        {canRemoveColleagueRelationship && (
          <Box
            position="absolute"
            top="3"
            right="0"
            zIndex="1"
            padding="0.5rem"
          >
            <RemoveColleagueRelationShip
              colleague={user}
              onSubmitEnd={() => {
                Router.replace(
                  {
                    pathname: Router.pathname,
                    query: Router.query,
                  },
                  Router.asPath
                );
              }}
            />
          </Box>
        )}
        <Show breakpoint="(max-width: 47em)">{badge && badge}</Show>
        <HStack spacing={{ base: '1rem', md: '1.25rem' }}>
          <Avatar
            src={`/data/avatars/${user.avatar?.filename}`}
            name={user.displayName}
            width={{ base: '4rem', md: '6rem' }}
            height={{ base: '4rem', md: '6rem' }}
            aspectRatio={1}
          />
          <VStack flexGrow={1} align="flex-start">
            <Text
              fontSize="1.25rem"
              fontWeight="600"
              color="brand.100"
              wordBreak="break-word"
            >
              {user.displayName}
            </Text>
            <HStack wrap="wrap">
              {user.jobs.length !== 0 && (
                <Text as="span" color="black.75">
                  {jobsLabel}
                </Text>
              )}
              {user.jobs.length !== 0 && user.country.id && (
                <Text
                  as="span"
                  display={{
                    base: 'none',
                    sm: isCardFullWidth ? 'inline' : null,
                  }}
                >
                  •
                </Text>
              )}
              {user.country.id && (
                <Text as="span" fontWeight="600">
                  # {user.country.name}
                </Text>
              )}
            </HStack>
            {disciplineLabels.length !== 0 && (
              <Flex gap="0.5rem">
                <Text fontWeight="600">
                  Discipline{disciplineLabels.length > 1 && 's'}&nbsp;:
                </Text>
                <List>
                  {disciplineLabels.map((discipline, index) => (
                    <ListItem key={index} wordBreak="break-word">
                      {discipline}
                      {disciplineLabels.length !== index + 1 && ','}
                    </ListItem>
                  ))}
                </List>
              </Flex>
            )}
          </VStack>
          <Show breakpoint="(min-width: 48em)">{badge && badge}</Show>
        </HStack>
        <VStack
          alignSelf="stretch"
          align={{ md: isCardFullWidth ? 'flex-end' : null }}
          spacing={{ base: '1rem', md: isCardFullWidth ? '1rem' : null }}
          marginBottom={{ md: isCardFullWidth ? '-.625rem' : null }}
        >
          <Flex flexWrap="wrap" gap="1rem" justify="flex-end" align="center">
            {showMemberRole && userRole != undefined && (
              <Badge variant={badgeVariants[userRole]} title="Statut">
                {humanizedRole}
              </Badge>
            )}
            {showMemberRole && isAttache && (
              <Badge variant="brand25" title="Statut">
                Attaché de coopération
              </Badge>
            )}
            {showMemberRole && isMemberExpert && (
              <Badge bg="brand100">Expert</Badge>
            )}
            {user.countPublications > 0 && (
              <>
                {isVisitorTheMember ? (
                  <NextLink
                    href={'/mes-publications#mes-publications'}
                    passHref
                  >
                    <Link>
                      <HStack as="span">
                        <Icon as={PiFilesThin} />
                        <Text as="span">
                          {user.countPublications} publication
                          {user.countPublications > 1 && 's'}
                        </Text>
                      </HStack>
                    </Link>
                  </NextLink>
                ) : (
                  <HStack as="span">
                    <Icon as={PiFilesThin} />
                    <Text as="span">
                      {user.countPublications} publication
                      {user.countPublications > 1 && 's'}
                    </Text>
                  </HStack>
                )}
              </>
            )}
          </Flex>
          {isVisitorTheMember && (
            <Flex justify="flex-end" marginTop="auto">
              <NextLink
                as={`/editer-profil`}
                href={{
                  pathname: '/editer-profil',
                }}
                passHref
              >
                <Button
                  as={'a'}
                  variant="outline"
                  size="sm"
                  marginTop="auto"
                  width="fit-content"
                >
                  Modifier mon profil
                </Button>
              </NextLink>
            </Flex>
          )}
          {!isVisitorTheMember && (
            <Flex
              flexGrow={1}
              justify={{ sm: 'end' }}
              align={{ sm: 'end' }}
              my="0.5rem"
              gap={'.5rem'}
            >
              {showDetailLink && (
                <NextLink
                  href={{
                    pathname: '/profil',
                    query: {
                      user: user.id,
                      country: user.country.code,
                      slug: user.slug,
                    },
                  }}
                  as={`/utilisateur/${user.slug}/${user.id}`}
                  passHref
                >
                  <Button
                    as={'a'}
                    size="xs"
                    pl="1em"
                    pr="1em"
                    variant={variant}
                    letterSpacing={{ base: -0.1, md: 'revert' }}
                  >
                    Consulter le profil
                  </Button>
                </NextLink>
              )}
              <ColleagueRelationship
                user={user}
                size="xs"
                variant={`${variant}Outline`}
              />
            </Flex>
          )}
        </VStack>
      </Flex>
    </SimpleCard>
  );
}
